// src/hooks/useRecentInvoiceAndReturns.ts
import { useCallback, useState } from "react";
import { getInvoicesByDateRange } from "../firebase/invoice/getInvoices";
import { getInvoiceByNumber } from "../firebase/invoice/getInvoiceByNumber";
import { getPaymentsByInvoiceNumber } from "../firebase/payment/getPaymentsByInvoiceNumber";
import { getReturnsByDateRange } from "../firebase/returns/getReturnsByDateRange";
import { getPaymentsByDateRange } from "../firebase/payment/getPaymentsByDateRange";
import { InvoiceData } from "../models/invoice";

export type RecentInvoiceAndReturnsEntry = {
	date: string;
	type: "Invoice" | "Return" | "Payment";
	details: string;
	amount: number;
	invoiceNumber: string;
	cash: number;
	upi: number;
	outstanding: number;
	profit: number;
};

export const useRecentInvoiceAndReturns = () => {
	const [ledgerEntries, setLedgerEntries] = useState<
		RecentInvoiceAndReturnsEntry[]
	>([]);
	const [loading, setLoading] = useState(true);

	const fetchLedgerData = useCallback(async (start: string, end: string) => {
		setLoading(true);
		try {
			// Load all source data for the selected range: invoices, returns, and payments.
			// These are the raw inputs used to build the ledger entry list.
			const invoices = (await getInvoicesByDateRange(start, end)) || {};
			const returnsInRange =
				(await getReturnsByDateRange(start, end)) || [];
			const paymentsInRange =
				(await getPaymentsByDateRange(start, end)) || [];

			// Convert each invoice into a ledger entry, including same-day cash/UPI payments
			// so that the invoice row shows immediate payment and outstanding correctly.
			const invoiceEntries: RecentInvoiceAndReturnsEntry[] =
				await Promise.all(
					Object.values(invoices).map(
						async (invoice: InvoiceData) => {
							const payments = await getPaymentsByInvoiceNumber(
								invoice.invoiceNumber
							);
							// Filter payments that are on the same date as the invoice
							const paymentsOfInvoiceDate = payments.filter(
								(p) =>
									new Date(p.paymentDate).toDateString() ===
									new Date(invoice.createdAt).toDateString()
							);

							const cashPayments = paymentsOfInvoiceDate
								.filter((p) => p.mode === "CASH")
								.reduce((sum, p) => sum + p.amount, 0);
							const upiPayments = paymentsOfInvoiceDate
								.filter((p) => p.mode === "UPI")
								.reduce((sum, p) => sum + p.amount, 0);
							const totalAmount = invoice.items.reduce(
								(total, item) =>
									total + item.sellPrice * item.quantity,
								0
							);
							const totalPurchasePrice = invoice.items.reduce(
								(total, item) =>
									total +
									(item.purchasePrice || 0) * item.quantity,
								0
							);
							const profit = totalAmount - totalPurchasePrice;
							const outstanding =
								totalAmount - cashPayments - upiPayments;
							return {
								date: invoice.createdAt,
								type: "Invoice",
								details: `${invoice.customerName}`,
								amount: totalAmount,
								invoiceNumber: invoice.invoiceNumber,
								cash: cashPayments,
								upi: upiPayments,
								outstanding: outstanding,
								profit: profit
							};
						}
					)
				);

			// Prepare return-specific ledger entry state.
			// "cashRefundUsedByInvoice" tracks how much refund was already recorded.
			// "outstandingCompensatedByInvoice" tracks how much outstanding has already been reduced.
			const returnEntries: RecentInvoiceAndReturnsEntry[] = [];
			const cashRefundUsedByInvoice: { [invoiceNumber: string]: number } =
				{};
			const outstandingCompensatedByInvoice: {
				[invoiceNumber: string]: number;
			} = {};

			// Process returns in chronological order so per-invoice refund tracking is correct
			returnsInRange.sort(
				(a, b) =>
					new Date(a.returnDate).getTime() -
					new Date(b.returnDate).getTime()
			);
			for (const ret of returnsInRange) {
				const invoice = await getInvoiceByNumber(ret.invoiceNumber);
				const invoiceTotal = invoice
					? invoice.items.reduce(
							(total, item) =>
								total + item.sellPrice * item.quantity,
							0
						)
					: 0;

				let returnAmount = ret.amount * ret.quantity; // Total line item return amount
				let profitLoss = 0;
				if (invoice) {
					const returnedItem = invoice.items.find(
						(item) => item.lineItemId === ret.lineItemId
					);
					if (returnedItem) {
						const purchasePrice = returnedItem.purchasePrice || 0;
						profitLoss =
							returnAmount - purchasePrice * ret.quantity;
					}
				}

				// Compute payments up to the return date.
				// This ensures only payments made before or on the return date influence
				// how much outstanding can be reduced and how much refund can be provided.
				const allPaymentsForInvoice = await getPaymentsByInvoiceNumber(
					ret.invoiceNumber
				);
				const paymentsForInvoice = allPaymentsForInvoice.filter(
					(p) =>
						new Date(p.paymentDate).getTime() <=
						new Date(ret.returnDate).getTime()
				);

				// All payments (CASH or UPI) are included when calculating total paid,
				// but return refunds are always represented as cash refunds in the ledger.
				const totalPaidForInvoice = paymentsForInvoice.reduce(
					(sum, p) => sum + p.amount,
					0
				);

				// Calculate original outstanding before return adjustments.
				const originalOutstanding = invoiceTotal - totalPaidForInvoice;
				// Calculate how much of the outstanding has already been compensated by previous returns.
				const alreadyCompensatedOutstanding =
					outstandingCompensatedByInvoice[ret.invoiceNumber] || 0;
				// Current outstanding after applying previous return reductions.
				const currentOutstanding =
					originalOutstanding - alreadyCompensatedOutstanding;

				// Return logic:
				// 1. Use the return to reduce outstanding first.
				// 2. If the return is larger than the remaining outstanding, refund the excess.
				const outstandingReduction = Math.min(
					Math.max(0, currentOutstanding),
					returnAmount
				);
				// cashRefundNeeded is the excess return amount after applying outstanding reduction.
				let cashRefundNeeded = Math.max(
					0,
					returnAmount - outstandingReduction
				);

				// Track cumulative outstanding reduction for this invoice.
				outstandingCompensatedByInvoice[ret.invoiceNumber] =
					alreadyCompensatedOutstanding + outstandingReduction;

				// Calculate how much of the previously paid amount is still available to refund.
				const alreadyRefundedCash =
					cashRefundUsedByInvoice[ret.invoiceNumber] || 0;
				// availableCashRefund is the remaining amount from prior payments that can be refunded.
				const availableCashRefund = Math.max(
					0,
					totalPaidForInvoice - alreadyRefundedCash
				);

				// Refund is the smaller of the required refund and the remaining available payment amount.
				const cashRefund = Math.min(
					cashRefundNeeded,
					availableCashRefund
				);

				// #sym:cashRefundNeeded vs #sym:availableCashRefund
				// cashRefundNeeded is how much refund is still required after paying down outstanding.
				// availableCashRefund is how much refund capacity remains from earlier payments.
				// The final cash refund is limited by the lesser of the two.

				cashRefundUsedByInvoice[ret.invoiceNumber] =
					alreadyRefundedCash + cashRefund;

				// Append a return ledger row that records return amount, refunded cash, outstanding reduction,
				// and the profit/loss impact of the returned item.
				returnEntries.push({
					date: ret.returnDate,
					type: "Return",
					details: `Return from Invoice ${ret.invoiceNumber}`,
					amount: -returnAmount,
					invoiceNumber: ret.invoiceNumber,
					cash: -cashRefund,
					upi: 0,
					outstanding: -outstandingReduction,
					profit: -profitLoss
				});
			}

			// Build separate ledger entries for payments in the range.
			// Skip same-day payments already displayed on the invoice row.
			// Build payment entries for all payments in range, excluding payments
			// already shown on same-day invoice rows to avoid duplication.
			const paymentEntries: RecentInvoiceAndReturnsEntry[] = (
				await Promise.all(
					paymentsInRange.map(async (payment) => {
						// Don't show payment entry if the invoice is already in the list and payment date is same as invoice date
						let invoice = Object.values(invoices).find(
							(inv) => inv.invoiceNumber === payment.invoiceNumber
						);
						if (
							invoice &&
							new Date(invoice.createdAt).toDateString() ===
								new Date(payment.paymentDate).toDateString()
						) {
							return null;
						}
						return {
							date: payment.paymentDate,
							type: "Payment",
							details: `Payment for Invoice ${payment.invoiceNumber} `, //(${invoice?.customerName})
							amount: 0,
							invoiceNumber: payment.invoiceNumber,
							cash: payment.mode === "CASH" ? payment.amount : 0,
							upi: payment.mode === "UPI" ? payment.amount : 0,
							outstanding: -payment.amount,
							profit: 0
						};
					})
				)
			).filter(Boolean) as RecentInvoiceAndReturnsEntry[];

			const combinedEntries = [
				...invoiceEntries,
				...returnEntries,
				...paymentEntries
			];
			combinedEntries.sort(
				(a, b) =>
					new Date(b.date).getTime() - new Date(a.date).getTime()
			);

			setLedgerEntries(combinedEntries);
		} catch (err) {
			console.error("Failed to load ledger data:", err);
		} finally {
			setLoading(false);
		}
	}, []);

	return { ledgerEntries, loading, fetchLedgerData };
};
